/* eslint-disable no-unused-vars */
import React, { useCallback, useMemo, useRef } from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Box from '@mui/material/Box';
import axios from 'axios';
import { API_URL } from '../../Config';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { Link, useNavigate } from 'react-router-dom';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import StepOne from './StepOne';
import StepTwo from './StepTwo';
import StepThree from './StepThree';
import StepFour from './StepFour';
import StepFive from './StepFive';
import StepSix from './StepSix';
import StepSeven from './StepSeven';
import { getAuthToken } from '../../Redux/Service/FetchBaseQuery';
import { useSelector, useDispatch } from 'react-redux';
import {
   clearContractTypePreFill,
   setContractUpdatePrefilledData,
} from '../../Redux/Reducers/Contracts/ContractSlice';
import { toast } from 'react-toastify';
import { CircularProgress } from '@mui/material';
import StyledDashboardLayout from '../style';

const steps = [
   'Contract Setup',
   'Commercial Details & Project Development',
   'Technology Stack',
   // 'Project Development Time',
   'Bank Details',
   'Terms & Conditions',
   'Upload Signature',
];

function ContractAdd() {
   const [activeStep, setActiveStep] = React.useState(0);
   const [skipped, setSkipped] = React.useState(new Set());
   const navigate = useNavigate();
   const [loading, setLoading] = React.useState(false);
   const formValuesRef = useRef(null);
   const setFieldValueRef = useRef(null);
   const formikRef = useRef(null);
   const [shouldRestore, setShouldRestore] = React.useState(false);
   const [formInitialized, setFormInitialized] = React.useState(false);

   const dispatch = useDispatch();

   // Clean Redux store before redirect
   // After success
   const cleanupAndRedirectToContractList = useCallback(async () => {
      dispatch(clearContractTypePreFill());
      dispatch(setContractUpdatePrefilledData(null));

      formValuesRef.current = null;
      setFieldValueRef.current = null;

      navigate('/contract');
   }, [dispatch, navigate]);

   // Redux States
   // Fix: Add null checks to avoid TypeError on refresh
   const signaturePath = useSelector((state) =>
      state.contractState?.prefilledData?.response
         ? state.contractState.prefilledData.response[7]?.data?.signature
         : '',
   );

   const contractTypePreFillData = useSelector(
      (state) => state.contractState?.contractTypePreFillData,
   );

   // Clean Redux store before redirect
   React.useEffect(() => {
      return () => {
         dispatch(clearContractTypePreFill());
         dispatch(setContractUpdatePrefilledData(null));
      };
   }, [dispatch]);

   const maxWithoutSpaces = (label, max = 5000) =>
      Yup.string()
         .required(`${label} is required`)
         .test(
            'max-without-spaces',
            `${label} is too long. Please keep it within ${max} characters`,
            (value = '') => value.replace(/\s/g, '').length <= max
         );

   React.useEffect(() => {
      if (shouldRestore && formValuesRef.current && setFieldValueRef.current) {
         try {
            Object.keys(formValuesRef.current).forEach((key) => {
               setFieldValueRef.current(key, formValuesRef.current[key], false);
            });
         } catch (restoreError) {
            console.error('Form restoration error:', restoreError);
         } finally {
            setShouldRestore(false);
         }
      }
   }, [shouldRestore]);

   let initialValues = useMemo(
      () => ({
         confidential_information: '',
         opportunity_id: '',
         currencyId: null, // Changed to number (optional)
         quotation_id: '',
         purpose_of_the_agreement: '',
         scope_of_work: '',
         amendments: '',
         project_development: '',
         commencementLabel: '',
         commencementValue: '',
         projectName: '',
         client_name: '',
         companyName: '',
         designation: '',
         address: '',
         note: '',
         deliverable_milestones: [
            {
               id: 1,
               milestone: '',
               // type: "Days",
               // delivery: 0,
               cost: 0,
            },
         ],
         proposed_technology_stack: [],
         team_structure: [
            {
               id: 1,
               member: '',
               engagement: '',
               count: 0,
            },
         ],
         dm_structure: [],
         total_development_cost: 0, // New field
         payable_tax_percentage: 0, // New field
         Bank_Details: [],
         general_terms_and_conditions: '',
         project_termination_terms: '',
         payment_terms: '',
         application_ownership: '',
         liability: '',
         legal_jurisdiction: '',
         uploadedFile: [''],
         // Prefill signature from Redux if available (could be a path string)
         signature: signaturePath || '',
         milestones: null, // Changed to number (optional)
         opportunity: null, // Changed to number (optional)
         estimate: null, // Changed to number (optional)
         proposal: null, // Changed to number (optional)
         serviceProviderId: '', // <-- Add this field
         totalTime: 0, // New optional field
         disclaimer: '', // New optional field
         contractTypeId: '', // Contract type for pre-fill
      }),
      [signaturePath],
   );

   const validationSchema = useMemo(
      () =>
         Yup.object().shape({
            contractTypeId: Yup.mixed().required('Contract Type is required'),
            opportunity_id: Yup.mixed().required('Opportunity is required'),
            projectName: Yup.string().required('Project Name is required').max(100, "Project name can't be more than 100 characters"),
            // address: Yup.string().required("Client Address is required"), // <-- Remove required
            serviceProviderId: Yup.number()
               .typeError('Service Provider is required')
               .required('Service Provider is required'),
            note: Yup.string().max(1000, "Notes is too long. Please keep it within 1000 characters").required("Note is required"),
            confidential_information: Yup.string().required().max(5000, "Confidential Information is too long. Please keep it within 5000 characters"),
            purpose_of_the_agreement: Yup.string().required().max(1000, "Purpose of the agreement is too long. Please keep it within 1000 characters"),
            scope_of_work: Yup.string().required().max(1000, "Scope of work is too long. Please keep it within 1000 characters"),
            amendments: Yup.string().required().max(5000, "Amendments is too long. Please keep it within 5000 characters"),
            total_development_cost: Yup.number().min(1, "Total Development Cost must be greater than 0").required('Total Development Cost is required').max(99999999, "Total Development Cost should be atmost 8 digits"),
            project_development: Yup.string().required().max(100, "Project Development is too long. Please keep it within 100 characters"),
            commencementValue: Yup.number().min(1, "Commencement Value must be greater than 0").required('Commencement Value is required').max(999999, "Commencement Date should be atmost 6 digits"),
            commencementLabel: Yup.string().required('Commencement Label is required').max(50, "Commencement Label is too long. Please keep it within 50 characters"),
            Bank_Details: Yup.array()
               .of(Yup.number().typeError('Each bank id must be a number'))
               .min(1, 'At least one bank account is required'),
            general_terms_and_conditions: maxWithoutSpaces('General Terms and Conditions'),
            project_termination_terms: maxWithoutSpaces('Project Termination Terms'),
            payment_terms: maxWithoutSpaces('Payment Terms'),
            application_ownership: maxWithoutSpaces('Application Ownership'),
            liability: maxWithoutSpaces('Liability'),
            legal_jurisdiction: maxWithoutSpaces('Legal Jurisdiction'),
            client_name: Yup.string().required().max(100, "Client Name is too long. Please keep it within 100 characters"),
            address: Yup.string().required().max(250, "Client Address is too long. Please keep it within 250 characters"),
            designation: Yup.string().required().max(250, "Designation is too long. Please keep it within 250 characters"),
            companyName: Yup.string().required().max(250, "Company Name is too long. Please keep it within 250 characters"),
            deliverable_milestones: Yup.array().of(
               Yup.object().shape({
                  milestone: Yup.string()
                     .required('Milestone name is required')
                     .max(50, 'Milestone name should be within 50 characters'),
                  cost: Yup.number()
                     .typeError('Cost must be a number')
                     .moreThan(0, 'Cost must be greater than 0')
                     .max(999999, 'Cost should have at most 6 digits')
                     .required('Cost is required'),
               }),
            ),

            // NEW: Team Structure validation
            team_structure: Yup.array().of(
               Yup.object().shape({
                  member: Yup.string()
                     .max(50, 'Team name should be within 50 characters'),
                  engagement: Yup.string()
                     .max(50, 'Engagement team should be within 50 characters'),
                  count: Yup.number()
                     .typeError('Number of team members must be a number')
                     .max(999, 'Number of team members cannot exceed 999')
                     .nullable(),
               }).test(
                  'team-row-required-together',
                  'Team name, engagement, and count are required for this row',
                  function (item) {
                     const { member, engagement, count } = item || {}
                     const anyFilled =
                        (member && member.trim() !== '') ||
                        (engagement && engagement.trim() !== '') ||
                        (count !== undefined && count !== null && String(count).trim() !== '' && Number(count) > 0)

                     if (!anyFilled) return true

                     const memberOk = !!(member && member.trim() !== '')
                     const engagementOk = !!(engagement && engagement.trim() !== '')
                     const countOk =
                        count !== undefined && count !== null &&
                        String(count).trim() !== '' && Number(count) > 0

                     return memberOk && engagementOk && countOk
                  },
               ),
            ),
            dm_structure: Yup.array().of(
               Yup.object().shape({
                  milname: Yup.string()
                     .max(50, 'Milestone name should be within 50 characters'),
                  delivery: Yup.number()
                     .typeError('Delivery must be a number')
                     .max(999, 'Delivery value cannot exceed 999')
                     .nullable(),
                  type: Yup.string(),
               }).test(
                  'dm-row-required-together',
                  'Milestone name and delivery are required for this row',
                  function (item) {
                     const { milname, delivery, type } = item || {}
                     const anyFilled =
                        (milname && milname.trim() !== '') ||
                        (delivery !== undefined && delivery !== null && String(delivery).trim() !== '' && Number(delivery) > 0) ||
                        (type && type.trim() !== '')
                     if (!anyFilled) return true
                     const milnameOk = !!(milname && milname.trim() !== '')
                     const deliveryOk =
                        delivery !== undefined && delivery !== null &&
                        String(delivery).trim() !== '' && Number(delivery) > 0
                     return milnameOk && deliveryOk
                  },
               ),
            ),
         }),
      [],
   );

   const isStepOptional = useCallback((step) => step === 1, []);

   const isStepSkipped = useCallback((step) => skipped.has(step), [skipped]);

   const handleNext = useCallback(
      (values) => {
         // check if the form is complete before moving to the next step
         if (activeStep === 0) {
            if (!values.contractTypeId) {
               if (formikRef.current) {
                  formikRef.current.setFieldTouched('contractTypeId', true);
               }
               toast.error('Please select a contract type');
               return;
            } else if (!values.opportunity) {
               if (formikRef.current) {
                  formikRef.current.setFieldTouched('opportunity_id', true);
               }
               toast.error('Please select a opportunity');
               return;
            } else if (!values.serviceProviderId) {
               if (formikRef.current) {
                  formikRef.current.setFieldTouched('serviceProviderId', true);
               }
               toast.error('Please select a service provider');
               return;
            } else if (!values.confidential_information) {
               if (formikRef.current) {
                  formikRef.current.setFieldTouched('confidential_information', true);
               }
               toast.error('Please fill the Confidential Information');
               return;
            } else if (values.confidential_information.length > 5000) {
               toast.error('Confidential Information should have at most 5000 characters');
               return;
            } else if (!values.purpose_of_the_agreement) {
               if (formikRef.current) {
                  formikRef.current.setFieldTouched('purpose_of_the_agreement', true);
               }
               toast.error('Please fill the Purpose of the Agreement');
               return;
            }
            else if (values.purpose_of_the_agreement.length > 1000) {
               toast.error('Purpose of the Agreement should have at most 1000 characters');
               return;
            } else if (!values.scope_of_work) {
               if (formikRef.current) {
                  formikRef.current.setFieldTouched('scope_of_work', true);
               }
               toast.error('Please fill the Scope of Work');
               return;
            } else if (!values.amendments) {
               if (formikRef.current) {
                  formikRef.current.setFieldTouched('amendments', true);
               }
               toast.error('Please fill the Amendments');
               return;
            } else if (values.amendments.length > 5000) {
               toast.error('Amendments should have at most 5000 characters');
               return;
            } else if (!values.projectName) {
               if (formikRef.current) {
                  formikRef.current.setFieldTouched('projectName', true);
               }
               toast.error('Please fill the Project Name');
               return;
            } else if (!values.projectName) {
               if (formikRef.current) {
                  formikRef.current.setFieldTouched('projectName', true);
               }
               toast.error('Please fill the Project Name');
               return;
            }
            else if (values.projectName.length > 100) {
               toast.error('Project Name should have at most 100 characters');
               return;
            } else if (values.scope_of_work.length > 1000) {
               toast.error('Scope of Work should have at most 1000 characters');
               return;
            } else if (values.client_name?.length > 100) {
               if (formikRef.current) {
                  formikRef.current.setFieldTouched('client_name', true);
               }
               toast.error('Client Name should have at most 100 characters');
               return;
            } else if (values.address?.length > 250) {
               if (formikRef.current) {
                  formikRef.current.setFieldTouched('address', true);
               }
               toast.error('Client Address should have at most 250 characters');
               return;
            } else if (values.companyName?.length > 250) {
               if (formikRef.current) {
                  formikRef.current.setFieldTouched('companyName', true);
               }
               toast.error('Company Name should have at most 250 characters');
               return;
            } else if (values.designation?.length > 250) {
               if (formikRef.current) {
                  formikRef.current.setFieldTouched('designation', true);
               }
               toast.error('Designation should have at most 250 characters');
               return;
            }
         } else if (activeStep === 1) {

            if (formikRef.current) {
               formikRef.current.setTouched({
                  ...formikRef.current.touched,
                  deliverable_milestones: values.deliverable_milestones.map(() => ({
                     milestone: true,
                     cost: true,
                  })),
               });
            }
            if (values.total_development_cost > 99999999) {
               toast.error('Total Development Cost should have at most 8 digits');
               return;
            }
            if (!values.total_development_cost || Number(values.total_development_cost) === 0) {
               if (formikRef.current) {
                  formikRef.current.setFieldTouched('total_development_cost', true);
               }
               toast.error('Please fill the Total Development Cost');
               return;
            }
            if (!values.project_development) {
               if (formikRef.current) {
                  formikRef.current.setFieldTouched('project_development', true);
               }
               toast.error('Please fill the Project Development Time');
               return;
            }
            // Validate deliverable milestones (optional but must be complete if present)
            for (let i = 0; i < values.dm_structure.length; i++) {
               const dm = values.dm_structure[i];
               if (!dm.milname) {
                  toast.error(`Deliverable Milestone row ${i + 1}: Please provide Milestone name`);
                  return;
               }
               if (dm.milname?.length > 50) {
                  toast.error(`Deliverable Milestone row ${i + 1}: Milestone name should have at most 50 characters`);
                  return;
               }
               if (dm.milname?.delivery > 999) {
                  toast.error(`Deliverable Milestone row ${i + 1}: Delivery  should have at most 3 digits`);
                  return;
               }
               if (!dm.delivery || Number(dm.delivery) === 0) {
                  toast.error(
                     `Deliverable Milestone row ${i + 1}: Delivery time must be greater than 0`,
                  );
                  return;
               }
            }
            if (values.commencementLabel.length > 50) {
               toast.error('Commencement Label should have at most 50 characters');
               return;
            }
            if (values.commencementValue > 999999) {
               toast.error('Commencement Value should have at most 6 digits');
               return;
            }
            // Commencement Label validation
            if (
               values.commencementLabel === '' ||
               values.commencementLabel === null ||
               values.commencementLabel === undefined
            ) {
               if (formikRef.current) {
                  formikRef.current.setFieldTouched('commencementLabel', true);
               }
               toast.error('Please put Commencement Label is Required');
               return;
            }
            // Commencement Value validation
            if (!values.commencementValue || Number(values.commencementValue) === 0) {
               if (formikRef.current) {
                  formikRef.current.setFieldTouched('commencementValue', true);
               }
               toast.error('Commencement Value is required');
               return;
            }
            // Validate payment milestones (required, must be complete)
            for (let i = 0; i < values.deliverable_milestones.length; i++) {
               const pm = values.deliverable_milestones[i];
               if (!pm.milestone) {
                  toast.error(`Payment Milestone row ${i + 1}: Please provide Milestone name`);
                  return;
               }
               if (pm.milestone?.length > 50) {
                  toast.error(`Payment Milestone row ${i + 1}: Milestone name should be within 50 characters`);
                  return;
               }
               if (pm.cost > 999999) {
                  toast.error(`Payment Milestone row ${i + 1}: Cost should have at most 6 digits`);
                  return;
               }
               if (pm.cost === '' || pm.cost === null || pm.cost === undefined) {
                  toast.error(`Payment Milestone row ${i + 1}: Please provide Cost value`);
                  return;
               }
               if (Number(pm.cost) === 0) {
                  toast.error(`Payment Milestone row ${i + 1}: Cost cannot be zero`);
                  return;
               }
            }
            // Add milestone + commencement validation for StepTwo
            const calculatedTaxAmount =
               (values.total_development_cost * (values.payable_tax_percentage || 0)) / 100;
            const totalAmountExcludingTax =
               Number(values.total_development_cost);
            const commencementFee = Number(values.commencementValue || 0);
            const totalMilestoneSum =
               values.deliverable_milestones.reduce(
                  (acc, curr) => acc + Number(curr.cost || 0),
                  0,
               ) + commencementFee;

            if (totalMilestoneSum !== totalAmountExcludingTax) {
               toast.error(
                  'Total (Milestones + Commencement Fee) should match the Total Development Cost excluding tax',
               );
               return;
            }
            if (!values.note) {
               if (formikRef.current) {
                  formikRef.current.setFieldTouched('note', true);
                  toast.error("Note is required");
                  return;
               }
            }
            if (values.note && values.note.length > 1000) {
               toast.error('Note should have at most 1000 characters');
               return;
            }
         } else if (activeStep === 2) {
            if (values.proposed_technology_stack.length === 0) {
               toast.error('Please fill the Proposed Technology Stack');
               return;
            }
            // Validate team structure - if any field is filled, all must be complete
            for (let i = 0; i < values.team_structure.length; i++) {
               const team = values.team_structure[i];
               if (team.member || team.engagement || team.count) {
                  if (!team.member) {
                     toast.error(`Team Structure row ${i + 1}: Please provide Team Name`);
                     return;
                  }
                  if (team.member?.length > 50) {
                     toast.error(`Team Structure row ${i + 1}: Team Name should be within 50 characters`);
                     return;
                  }

                  if (team.engagement?.length > 50) {
                     toast.error(`Team Structure row ${i + 1}: Engagement should be within 50 characters`);
                     return;
                  }

                  if (team.count > 999) {
                     toast.error(`Team Structure row ${i + 1}: Count should have at most 3 digits`);
                     return;
                  }
                  if (!team.engagement) {
                     toast.error(`Team Structure row ${i + 1}: Please provide Engagement Team`);
                     return;
                  }
                  if (!team.count || Number(team.count) === 0) {
                     toast.error(
                        `Team Structure row ${i + 1}: Number of team members must be greater than 0`,
                     );
                     return;
                  }
               }
            }
         }
         // else if (activeStep === 3) {
         //    if (!values.project_development) {
         //       toast.error('Please fill the Project Development Time');
         //       return;
         //    }
         //    // Validate deliverable milestones (optional but must be complete if present)
         //    for (let i = 0; i < values.dm_structure.length; i++) {
         //       const dm = values.dm_structure[i];
         //       if (!dm.milname) {
         //          toast.error(`Deliverable Milestone row ${i + 1}: Please provide Milestone name`);
         //          return;
         //       }
         //       if (!dm.delivery || Number(dm.delivery) === 0) {
         //          toast.error(
         //             `Deliverable Milestone row ${i + 1}: Delivery time must be greater than 0`,
         //          );
         //          return;
         //       }
         //    }
         //    // Commencement Label validation
         //    if (
         //       values.commencementLabel === '' ||
         //       values.commencementLabel === null ||
         //       values.commencementLabel === undefined
         //    ) {
         //       toast.error('Please put Commencement Label is Required');
         //       return;
         //    }
         //    // Commencement Value validation
         //    if (!values.commencementValue || Number(values.commencementValue) === 0) {
         //       toast.error('Commencement Value is required');
         //       return;
         //    }
         //    // Validate payment milestones (required, must be complete)
         //    for (let i = 0; i < values.deliverable_milestones.length; i++) {
         //       const pm = values.deliverable_milestones[i];
         //       if (!pm.milestone) {
         //          toast.error(`Payment Milestone row ${i + 1}: Please provide Milestone name`);
         //          return;
         //       }
         //       if (pm.cost === '' || pm.cost === null || pm.cost === undefined) {
         //          toast.error(`Payment Milestone row ${i + 1}: Please provide Cost value`);
         //          return;
         //       }
         //       if (Number(pm.cost) === 0) {
         //          toast.error(`Payment Milestone row ${i + 1}: Cost cannot be zero`);
         //          return;
         //       }
         //    }
         //    // Add milestone + commencement validation for StepTwo
         //    const calculatedTaxAmount =
         //       (values.total_development_cost * (values.payable_tax_percentage || 0)) / 100;
         //    const totalAmountIncludingTax =
         //       Number(values.total_development_cost) + Number(calculatedTaxAmount);
         //    const commencementFee = Number(values.commencementValue || 0);
         //    const totalMilestoneSum =
         //       values.deliverable_milestones.reduce(
         //          (acc, curr) => acc + Number(curr.cost || 0),
         //          0,
         //       ) + commencementFee;

         //    if (totalMilestoneSum !== totalAmountIncludingTax) {
         //       toast.error(
         //          'Total (Milestones + Commencement Fee) should match the Total Development Cost including tax',
         //       );
         //       return;
         //    // }
         // } 
         else if (activeStep === 3) {
            if (values.Bank_Details.length === 0) {
               if (formikRef.current) {
                  formikRef.current.setFieldTouched('Bank_Details', true);
               }
               toast.error('Please fill the Bank Details');
               return;
            }
         } else if (activeStep === 4) {
            const lengthWithoutSpaces = (value = '') =>
               String(value).replace(/\s/g, '').length;

            const validations = [
               ['general_terms_and_conditions', 'General Terms and Conditions'],
               ['project_termination_terms', 'Project Termination Terms'],
               ['payment_terms', 'Payment Terms'],
               ['application_ownership', 'Application Ownership'],
               ['liability', 'Liability'],
               ['legal_jurisdiction', 'Legal Jurisdiction'],
            ];

            for (const [field, label] of validations) {
               if (!values[field]) {
                  if (formikRef.current) {
                     formikRef.current.setFieldTouched(field, true);
                  }

                  toast.error(`Please fill the ${label}`);
                  return;
               }

               if (lengthWithoutSpaces(values[field]) > 5000) {
                  if (formikRef.current) {
                     formikRef.current.setFieldTouched(field, true);
                  }

                  toast.error(`${label} should have at most 5000 characters`);
                  return;
               }
            }
         }

         if (activeStep < steps.length) {
            setActiveStep((prevActiveStep) => prevActiveStep + 1);
         }
      },
      [activeStep, steps.length],
   );

   const handleBack = useCallback(() => {
      if (activeStep > 0) {
         setActiveStep((prevActiveStep) => prevActiveStep - 1);
      }
   }, [activeStep]);

   const handleSubmit = useCallback(
      async (values) => {
         formValuesRef.current = { ...values };

         // Validate team structure completeness
         for (let i = 0; i < values.team_structure.length; i++) {
            const team = values.team_structure[i];
            if (team.member || team.engagement || team.count) {
               if (!team.member) {
                  toast.error(`Team Structure row ${i + 1}: Please provide Team Name`);
                  return;
               }
               if (!team.engagement) {
                  toast.error(`Team Structure row ${i + 1}: Please provide Engagement Team`);
                  return;
               }
               if (!team.count || Number(team.count) === 0) {
                  toast.error(
                     `Team Structure row ${i + 1}: Number of team members must be greater than 0`,
                  );
                  return;
               }
            }
         }

         // Validate deliverable milestones completeness
         for (let i = 0; i < values.dm_structure.length; i++) {
            const dm = values.dm_structure[i];
            if (!dm.milname) {
               toast.error(`Deliverable Milestone row ${i + 1}: Please provide Milestone name`);
               return;
            }
            if (!dm.delivery || Number(dm.delivery) === 0) {
               toast.error(
                  `Deliverable Milestone row ${i + 1}: Delivery time must be greater than 0`,
               );
               return;
            }
         }

         // Validate payment milestones completeness
         for (let i = 0; i < values.deliverable_milestones.length; i++) {
            const pm = values.deliverable_milestones[i];
            if (!pm.milestone) {
               toast.error(`Payment Milestone row ${i + 1}: Please provide Milestone name`);
               return;
            }
            if (pm.cost === '' || pm.cost === null || pm.cost === undefined) {
               toast.error(`Payment Milestone row ${i + 1}: Please provide Cost value`);
               return;
            }
            if (Number(pm.cost) === 0) {
               toast.error(`Payment Milestone row ${i + 1}: Cost cannot be zero`);
               return;
            }
         }

         // Validate required fields with specific messages
         const requiredFields = {
            projectName: 'Project Name',
            confidential_information: 'Confidential Information',
            purpose_of_the_agreement: 'Purpose of Agreement',
            scope_of_work: 'Scope of Work',
            amendments: 'Amendments',
            total_development_cost: 'Total Development Cost',
            proposed_technology_stack: 'Technology Stack',
            project_development: 'Project Development Time',
            commencementLabel: 'Commencement Label',
            commencementValue: 'Commencement Value',
            deliverable_milestones: 'Milestones',
            Bank_Details: 'Bank Details',
            general_terms_and_conditions: 'General Terms & Conditions',
            project_termination_terms: 'Termination Terms',
            payment_terms: 'Payment Terms',
            application_ownership: 'Application Ownership',
            liability: 'Liability',
            legal_jurisdiction: 'Legal Jurisdiction',
            serviceProviderId: 'Service Provider',
         };

         for (const [field, label] of Object.entries(requiredFields)) {
            const value = values[field];

            // Check string fields
            if (typeof value === 'string' && value.trim() === '') {
               toast.error(`Please fill ${label}`);
               return;
            }

            // Check array fields (must have at least one item)
            if (Array.isArray(value) && value.length === 0) {
               toast.error(`Please fill ${label}`);
               return;
            }

            // Check numeric fields (must be greater than 0)
            if (typeof value === 'number' && value === 0) {
               if (field === 'commencementValue' || field === 'total_development_cost') {
                  toast.error(`Please fill ${label}`);
                  return;
               }
            }
         }

         // check if user has uploaded any file
         if (!values.uploadedFile || values.uploadedFile.length === 0) {
            toast.error('Please upload at least one document');
            return;
         }

         // Create a new contract formData
         const contractFormData = new FormData();

         // Calculate values (validation already done in handleNext)
         const calculatedTaxAmount =
            (values.total_development_cost * (values.payable_tax_percentage || 0)) / 100;
         const commencementFee = Number(values.commencementValue || 0);

         // check that if total Milestone cost is matching with the total cost of Commercial Details with GST Percentage
         const totalAmountIncludingTax =
            Number(values.total_development_cost)

         // Add commencementValue to milestone sum

         // Billable amount is now milestone sum + commencementValue
         const totalMilestoneSum =
            values.deliverable_milestones.reduce((acc, curr) => acc + Number(curr.cost || 0), 0) +
            commencementFee;

         if (totalMilestoneSum !== totalAmountIncludingTax) {
            toast.error(
               'Total (Milestones + Commencement Fee) should match the Total Development Cost including tax',
            );
            return;
         }

         // Append the values to the formData (normalize CKEditor content)
         // Only append signature if it exists and is a file
         // (removed duplicate early append) signature will be appended later with proper checks

         contractFormData.append(
            'opportunity',
            values.opportunity_id ? Number(values.opportunity_id) : null,
         );
         contractFormData.append(
            'contract_type_id',
            values.contractTypeId ? Number(values.contractTypeId) : null,
         );
         contractFormData.append(
            'quotation',
            values.quotation_id ? Number(values.quotation_id) : null,
         );
         contractFormData.append('currencyId', values.currencyId ? Number(values.currencyId) : '');
         contractFormData.append(
            'confidentialInfo',
            values.confidential_information
               ? normalizeEditorContent(values.confidential_information)
               : '',
         );
         contractFormData.append(
            'purposeAgreement',
            values.purpose_of_the_agreement
               ? normalizeEditorContent(values.purpose_of_the_agreement)
               : '',
         );
         contractFormData.append(
            'scopeWork',
            values.scope_of_work
               ? normalizeEditorContent(values.scope_of_work) // keep CKEditor HTML
               : '',
         );
         contractFormData.append(
            'amendment',
            values.amendments ? normalizeEditorContent(values.amendments) : '',
         );

         // Add These data in data base
         contractFormData.append('proposal', values.proposal ? Number(values.proposal) : null);
         contractFormData.append('estimate', values.estimate ? Number(values.estimate) : null);
         contractFormData.append(
            'milestones',
            values.milestones ? Number(values.milestones) : null,
         );

         contractFormData.append(
            'techStack',
            JSON.stringify(values.proposed_technology_stack || []),
         );
         // Filter out empty team entries (where member, engagement, and count are all empty/zero)
         const filteredTeam = values.team_structure
            .filter((team) => team.member || team.engagement || team.count)
            .map((team) => ({
               ...team,
               count: Number(team.count) || 0, // Ensure count is a number
            }));
         contractFormData.append('team', JSON.stringify(filteredTeam));
         contractFormData.append('dm', JSON.stringify(values.dm_structure));
         contractFormData.append(
            'milestone',
            JSON.stringify(
               (values.deliverable_milestones || []).map((m) => ({
                  ...m,
                  cost: Number(m.cost) || 0, // Ensure cost is a number
                  id: m.id ? Number(m.id) : undefined, // Convert id to number if present
               })),
            ),
         );
         // Convert bank array: ensure each element is a number
         const bankArray = (values.Bank_Details || []).map((b) =>
            typeof b === 'number' ? b : Number(b),
         );
         contractFormData.append('bank', JSON.stringify(bankArray));
         // Helper: parse CKEditor HTML into an array of items (preserve list items or paragraphs)
         function parseHtmlToArray(html) {
            try {
               if (!html) return [];
               const parser = new DOMParser();
               const doc = parser.parseFromString(String(html), 'text/html');

               // Prefer list items if present
               const liItems = Array.from(doc.querySelectorAll('li'))
                  .map((li) => li.textContent.trim())
                  .filter(Boolean);
               if (liItems.length) return liItems;

               // Fallback to paragraph tags
               const pItems = Array.from(doc.querySelectorAll('p'))
                  .map((p) => p.textContent.trim())
                  .filter(Boolean);
               if (pItems.length) return pItems;

               // Final fallback: split by newlines or sentence endings
               const text =
                  doc.body && doc.body.textContent
                     ? doc.body.textContent.trim()
                     : String(html)
                        .replace(/<[^>]+>/g, '')
                        .trim();
               if (!text) return [];
               const byNewline = text
                  .split(/\n+/)
                  .map((s) => s.trim())
                  .filter(Boolean);
               if (byNewline.length > 1) return byNewline;
               const bySentence = text
                  .split(/(?:\.\s+|\n+)/)
                  .map((s) => s.trim())
                  .filter(Boolean);
               return bySentence;
            } catch (e) {
               // As a last resort, return the raw text split by sentences
               const plain = String(html).replace(/<[^>]+>/g, '');
               return plain
                  .split(/(?:\.\s+|\n+)/)
                  .map((s) => s.trim())
                  .filter(Boolean);
            }
         }

         // Append arrays (preserve as arrays for backend: general_condition, payment_term_condition, project_termination_term)
         const generalArr = parseHtmlToArray(values.general_terms_and_conditions);
         contractFormData.append('general_term', JSON.stringify(generalArr));

         contractFormData.append('taxAmount', Number(calculatedTaxAmount));

         const terminationArr = parseHtmlToArray(values.project_termination_terms);
         contractFormData.append('termination_term', JSON.stringify(terminationArr));

         const paymentArr = parseHtmlToArray(values.payment_terms);
         contractFormData.append('payment_term', JSON.stringify(paymentArr));
         contractFormData.append(
            'app_ownership',
            normalizeEditorContent(values.application_ownership),
         );
         contractFormData.append('liability', normalizeEditorContent(values.liability));
         contractFormData.append(
            'legal_jurisdiction',
            normalizeEditorContent(values.legal_jurisdiction),
         );
         contractFormData.append('billableAmount', Number(values.total_development_cost));
         contractFormData.append(
            'developementTime',
            normalizeEditorContent(values.project_development || ''),
         );

         contractFormData.append('commencementLabel', values.commencementLabel || '');
         contractFormData.append('note', values.note || '');
         contractFormData.append(
            'commencementValue',
            values.commencementValue ? Number(values.commencementValue) : 0,
         );
         contractFormData.append('projectName', values.projectName || '');
         contractFormData.append('client_name', values.client_name || '');
         contractFormData.append('companyName', values.companyName || '');
         contractFormData.append('designation', values.designation || '');
         contractFormData.append('address', values.address || '');
         contractFormData.append('serviceProviderId', Number(values.serviceProviderId));
         contractFormData.append('totalTime', Number(values.totalTime) || 0);
         contractFormData.append('disclaimer', values.disclaimer || '');

         // Append uploaded milestone files
         for (let i = 0; i < values.uploadedFile.length; i++) {
            if (values.uploadedFile[i] && values.uploadedFile[i] instanceof File) {
               contractFormData.append(`image${i}`, values.uploadedFile[i]);
            }
         }

         // Append signature at the end to ensure proper processing
         // Support File upload, string path, or fallback to prefilled signaturePath
         if (values.signature && values.signature instanceof File) {
            contractFormData.append('signature', values.signature);
         } else if (typeof values.signature === 'string' && values.signature.trim() !== '') {
            // send signature as a path/string when user selected an existing signature
            contractFormData.append('signature', values.signature);
         } else if (signaturePath) {
            // fallback to Redux-provided signaturePath
            contractFormData.append('signature', signaturePath);
         }

         try {
            setLoading(true); // Start loading
            const token = getAuthToken();
            const response = await axios.post(`${API_URL}contract/create`, contractFormData, {
               headers: {
                  'Content-Type': 'multipart/form-data',
                  Authorization: `Bearer ${token}`,
                  token,
               },
            });

            // Send PDF Generate Request to the server
            await axios.post(
               `${API_URL}contract/generate/${response.data.response.id}`,
               {},
               {
                  headers: {
                     Authorization: `Bearer ${token}`,
                     token,
                  },
               },
            );

            setLoading(false); // Stop loading

            toast.success('Contract created successfully');
            await cleanupAndRedirectToContractList();
         } catch (error) {
            setLoading(false);

            if (formValuesRef.current && setFieldValueRef.current) {
               setShouldRestore(true);
            }

            toast.error(error.response.data.message);
            console.log('Error Creating Contract:', error);
         }
      },
      [cleanupAndRedirectToContractList, signaturePath],
   );

   const StepContent = useCallback(
      ({ isSubmitting, values, setFieldValue, touched, errors, initialValues, handleBlur }) => {
         switch (activeStep) {
            case 0:
               return (
                  <StepOne
                     {...{
                        isSubmitting,
                        values,
                        setFieldValue,
                        touched,
                        errors,
                        initialValues,
                        handleBlur,
                     }}
                  />
               );
            case 1:
               return (
                  <StepFour
                     {...{
                        isSubmitting,
                        values,
                        setFieldValue,
                        touched,
                        errors,
                        initialValues,
                        handleBlur,
                     }}
                  />
               );
            case 2:
               return (
                  <StepThree
                     {...{
                        isSubmitting,
                        values,
                        setFieldValue,
                        touched,
                        errors,
                        initialValues,
                        handleBlur,
                     }}
                  />
               );
            case 3:
               return (
                  <StepFive
                     {...{
                        isSubmitting,
                        values,
                        setFieldValue,
                        touched,
                        errors,
                        initialValues,
                        handleBlur,
                     }}
                  />
               );
            case 4:
               return (
                  <StepSix
                     {...{
                        isSubmitting,
                        values,
                        setFieldValue,
                        touched,
                        errors,
                        initialValues,
                        handleBlur,
                     }}
                  />
               );
            case 5:
               return (
                  <StepSeven
                     {...{
                        isSubmitting,
                        values,
                        setFieldValue,
                        touched,
                        errors,
                        initialValues,
                        handleBlur,
                     }}
                  />
               );
            // case 6:
            //    return (
            //       <StepSeven
            //          {...{
            //             isSubmitting,
            //             values,
            //             setFieldValue,
            //             touched,
            //             errors,
            //             initialValues,
            //             handleBlur,
            //          }}
            //       />
            //    );
            default:
               return null;
         }
      },
      [activeStep],
   );

   // Add a function to handle home button click
   const handleHomeClick = useCallback(async () => {
      if (activeStep === 0) {
         await cleanupAndRedirectToContractList();
      } else {
         setActiveStep((prevStep) => prevStep - 1);
      }
   }, [activeStep, cleanupAndRedirectToContractList]);

   // Guard: Check if navigated from ContractList
   React.useEffect(() => {
      const redirectIfInvalidSource = async () => {
         if (!window.contractListSource) {
            await cleanupAndRedirectToContractList();
         }
      };

      redirectIfInvalidSource();
   }, [cleanupAndRedirectToContractList]);

   if (!window.contractListSource) {
      return null;
   }

   return (
      <StyledDashboardLayout>
         <div className="content-wrapper">
            {loading ? (
               <div className="loading-screen">
                  <CircularProgress /> {/* Show loading spinner */}
                  <p>Processing your request. Please wait...</p>
               </div>
            ) : (
               <>
                  <div className="content-header">
                     <div className="container-fluid">
                        <div className="d-flex justify-content-between mb-2">
                           <div className="pageTitle">
                              <h1 className="m-0">Add Contract</h1>
                           </div>

                           <Button
                              onClick={handleHomeClick}
                              startIcon={<ArrowBackIcon />}
                              sx={{
                                 display: 'flex',
                                 alignItems: 'center',
                                 gap: '8px',
                                 padding: '10px 20px',
                                 minWidth: '160px',
                                 height: '44px',
                                 backgroundColor: '#3F30FF',
                                 borderRadius: '8px',
                                 color: '#ffffff',
                                 border: '2px solid #3F30FF',
                                 textTransform: 'none',
                                 fontSize: '14px',
                                 fontWeight: '600',
                                 letterSpacing: '0.5px',
                                 boxShadow: '0 4px 12px rgba(63, 48, 255, 0.4)',
                                 cursor: 'pointer',
                                 transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                 position: 'relative',
                                 overflow: 'hidden',
                                 '&:before': {
                                    content: '""',
                                    position: 'absolute',
                                    top: 0,
                                    left: '-100%',
                                    width: '100%',
                                    height: '100%',
                                    background:
                                       'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                                    transition: 'left 0.5s',
                                 },
                                 '&:hover': {
                                    backgroundColor: '#3F30FF',
                                    borderColor: '#3F30FF',

                                    '&:before': {
                                       left: '100%',
                                    },
                                 },
                                 '& .MuiButton-startIcon': {
                                    marginRight: '8px',
                                    fontSize: '18px',
                                    color: '#ffffff',
                                    transition: 'transform 0.3s ease',
                                 },
                                 '&:hover .MuiButton-startIcon': {
                                    transform: 'translateX(-2px)',
                                 },
                              }}
                           >
                              {activeStep === 0
                                 ? 'Back to Contracts'
                                 : `Back to ${steps[activeStep - 1]}`}
                           </Button>
                        </div>
                     </div>
                  </div>
                  <div className="card customCard p-3 m-3">
                     <Box sx={{ width: '100%' }}>
                        <Stepper activeStep={activeStep} sx={{ marginBottom: '20px' }}>
                           {steps.map((label, index) => {
                              const stepProps = {};
                              const labelProps = {};

                              // Highlight active step with custom styles
                              if (index === activeStep) {
                                 labelProps.sx = {
                                    color: '#3F30FF', // Change active step color
                                    fontWeight: 'bold', // Add bold text for emphasis
                                 };
                              }

                              return (
                                 <Step
                                    key={label}
                                    {...stepProps}
                                    onClick={() => {
                                       if (index < activeStep) {
                                          setActiveStep(index); // Move to the clicked step
                                          setSkipped(new Set()); // Reset skipped steps
                                       }
                                    }}
                                    sx={{
                                       cursor: index < activeStep ? 'pointer' : 'default',
                                       '&:hover': {
                                          backgroundColor:
                                             index < activeStep ? '#f5f5f5' : 'transparent',
                                       },
                                    }}
                                 >
                                    <StepLabel {...labelProps}>{label}</StepLabel>
                                 </Step>
                              );
                           })}
                        </Stepper>

                        <Formik
                           innerRef={formikRef}
                           initialValues={initialValues}
                           validationSchema={validationSchema}
                           enableReinitialize={true}
                        >
                           {({
                              isSubmitting,
                              values,
                              setFieldValue,
                              touched,
                              errors,
                              handleBlur,
                           }) => {
                              if (!formInitialized && values.general_terms_and_conditions) {
                                 setFormInitialized(true);
                              }
                              setFieldValueRef.current = setFieldValue;

                              return (
                                 <Form>
                                    <StepContent
                                       {...{
                                          isSubmitting,
                                          values,
                                          setFieldValue,
                                          touched,
                                          errors,
                                          initialValues,
                                          handleBlur,
                                       }}
                                    />
                                    <Box
                                       sx={{
                                          display: 'flex',
                                          flexDirection: 'row',
                                          gap: '10px',
                                          pt: 2,
                                       }}
                                    >
                                       <Button
                                          color="inherit"
                                          disabled={activeStep === 0}
                                          onClick={handleBack}
                                          sx={{ mr: 1 }}
                                          className="backBtn"
                                       >
                                          Back
                                       </Button>
                                       <Box />
                                       {activeStep === steps.length - 1 ? (
                                          <Button
                                             type="button"
                                             onClick={() => handleSubmit(values)}
                                             className="commonBtn"
                                          >
                                             Submit
                                          </Button>
                                       ) : (
                                          <Button
                                             type="button"
                                             onClick={() => handleNext(values)}
                                             className="commonBtn"
                                          >
                                             Next
                                          </Button>
                                       )}
                                    </Box>
                                 </Form>
                              );
                           }}
                        </Formik>
                     </Box>
                  </div>
               </>
            )}
         </div>
      </StyledDashboardLayout>
   );
}

export default React.memo(ContractAdd);

// Helper: preserve user-entered spacing. Convert &nbsp; entity to NBSP char and do not collapse/trim spaces.
function normalizeEditorContent(value = '') {
   return String(value).replace(/&nbsp;/g, '\u00A0');
}

// Deprecated: Use inline validation in handleSubmit instead
// Keeping for reference, can be removed in future refactor
function isObjectEmptyExceptSignature(obj) {
   for (let key in obj) {
      if (key === 'signature') continue;
      if (key === 'milestones') continue;
      if (key === 'estimate') continue;
      if (key === 'quotation_id') continue;
      if (key === 'proposal') continue;
      if (key === 'designation') continue;
      if (key === 'address') continue;
      if (key === 'note') continue;

      const value = obj[key];

      if (typeof value == 'string' && value.trim() === '') {
         return false;
      }

      if (key === 'uploadedFile' && Array.isArray(value)) {
         if (value.length === 0) {
            return false;
         }
      }
   }
   return true;
}
